// Timestamp block code burned into every broadcast frame (pure, no DOM).
//
// Value: ms = round(serverNowMs) mod 2^24, sent as 24 data bits + CRC-6 (poly 0x03, init 0) = 30 bits.
// Grid: 8 cols x 4 rows of S x S cells at (S, S), S = max(8, round(frameW / 32)), row-major.
// Cell 0 = white, cell 1 = black (calibration), cells 2..31 = bits MSB-first (1 = white).
// A one-cell mid-grey border surrounds the grid, so the whole stamp is the box (0,0)-(10S,6S).

export const STAMP_MOD = 2 ** 24;
const HALF_MOD = 2 ** 23;
const DATA_BITS = 24;
const CRC_BITS = 6;
const CRC_POLY = 0x03;
export const STAMP_BITS = DATA_BITS + CRC_BITS;
const COLS = 8;
const ROWS = 4;
const MIN_CONFIDENCE = 0.25;

const WHITE = '#ffffff';
const BLACK = '#000000';
const GREY = '#808080';

export function stampLayout(frameW, frameH) {
  const S = Math.max(8, Math.round(frameW / 32));
  return { x: S, y: S, S, cols: COLS, rows: ROWS };
}

// Top-left rectangle that holds the whole stamp (border included) for every cell size
// readStamp may try. Use it for getImageData, then pass that data with the FRAME size.
export function stampRegion(frameW, frameH) {
  const S = Math.max(stampLayout(frameW, frameH).S, frameW / 32);
  return {
    x: 0,
    y: 0,
    w: Math.max(1, Math.min(frameW, Math.ceil((COLS + 2) * S))),
    h: Math.max(1, Math.min(frameH, Math.ceil((ROWS + 2) * S))),
  };
}

function wrapMs(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n)) throw new TypeError('stamp value must be a finite number');
  const r = Math.round(n) % STAMP_MOD;
  return r < 0 ? r + STAMP_MOD : r;
}

function crc6(bits, count) {
  let reg = 0;
  for (let i = 0; i < count; i++) {
    const fb = ((reg >> (CRC_BITS - 1)) & 1) ^ (bits[i] ? 1 : 0);
    reg = (reg << 1) & 0x3f;
    if (fb) reg ^= CRC_POLY;
  }
  return reg;
}

export function encodeBits(ms) {
  const v = wrapMs(ms);
  const bits = new Array(STAMP_BITS);
  for (let i = 0; i < DATA_BITS; i++) bits[i] = (v >> (DATA_BITS - 1 - i)) & 1;
  const crc = crc6(bits, DATA_BITS);
  for (let i = 0; i < CRC_BITS; i++) bits[DATA_BITS + i] = (crc >> (CRC_BITS - 1 - i)) & 1;
  return bits;
}

export function decodeBits(bits) {
  if (!bits || bits.length < STAMP_BITS) return { ms: null, crcOk: false };
  let v = 0;
  for (let i = 0; i < DATA_BITS; i++) v = v * 2 + (bits[i] ? 1 : 0);
  let rx = 0;
  for (let i = 0; i < CRC_BITS; i++) rx = rx * 2 + (bits[DATA_BITS + i] ? 1 : 0);
  return { ms: v, crcOk: crc6(bits, DATA_BITS) === rx };
}

// Only uses ctx.fillStyle and ctx.fillRect. Restores fillStyle afterwards.
export function drawStamp(ctx, frameW, frameH, serverNowMs) {
  const { x, y, S } = stampLayout(frameW, frameH);
  const bits = encodeBits(serverNowMs);
  const prevFill = ctx.fillStyle;

  ctx.fillStyle = GREY;
  ctx.fillRect(x - S, y - S, (COLS + 2) * S, (ROWS + 2) * S);

  for (const colour of [WHITE, BLACK]) {
    const want = colour === WHITE ? 1 : 0;
    ctx.fillStyle = colour;
    for (let i = 0; i < COLS * ROWS; i++) {
      const on = i === 0 ? 1 : i === 1 ? 0 : bits[i - 2];
      if (on !== want) continue;
      ctx.fillRect(x + (i % COLS) * S, y + Math.floor(i / COLS) * S, S, S);
    }
  }
  ctx.fillStyle = prevFill;
}

// Works out the row stride of `rgba`: either the full frame, or a top-left region of it
// (for example the stampRegion() rectangle).
function bufferGeometry(rgba, frameW, frameH) {
  const px = Math.floor(rgba.length / 4);
  if (px === frameW * frameH) return { stride: frameW, rows: frameH };
  const S = stampLayout(frameW, frameH).S;
  const alt = frameW / 32;
  const minRows = Math.min(frameH, Math.floor((ROWS + 1) * Math.min(S, alt)) - 1);
  const widths = [
    stampRegion(frameW, frameH).w,
    Math.ceil((COLS + 2) * S),
    Math.ceil((COLS + 1) * S),
    Math.ceil((COLS + 2) * alt),
    Math.ceil((COLS + 1) * alt),
    frameW,
  ];
  for (const w of widths) {
    if (w > 0 && w <= frameW && px % w === 0) {
      const rows = px / w;
      if (rows >= minRows && rows <= frameH) return { stride: w, rows };
    }
  }
  return null;
}

function cellSpan(start, S, limit) {
  const a = start + S / 4;
  const b = start + (3 * S) / 4;
  let lo = Math.ceil(a - 0.5);
  let hi = Math.ceil(b - 0.5) - 1;
  if (hi < lo) lo = hi = Math.floor((a + b) / 2);
  if (lo < 0 || hi >= limit) return null;
  return [lo, hi];
}

function readWithCellSize(rgba, stride, rows, S) {
  const n = COLS * ROWS;
  const luma = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const xs = cellSpan(S + (i % COLS) * S, S, stride);
    const ys = cellSpan(S + Math.floor(i / COLS) * S, S, rows);
    if (!xs || !ys) return null;
    let sum = 0;
    let count = 0;
    for (let yy = ys[0]; yy <= ys[1]; yy++) {
      let o = (yy * stride + xs[0]) * 4;
      for (let xx = xs[0]; xx <= xs[1]; xx++, o += 4) {
        sum += 0.299 * rgba[o] + 0.587 * rgba[o + 1] + 0.114 * rgba[o + 2];
        count++;
      }
    }
    luma[i] = sum / count;
  }
  const white = luma[0];
  const black = luma[1];
  const confidence = Math.abs(white - black) / 255;
  const threshold = (white + black) / 2;
  const bits = new Array(STAMP_BITS);
  for (let k = 0; k < STAMP_BITS; k++) bits[k] = luma[k + 2] > threshold ? 1 : 0;
  const { ms, crcOk } = decodeBits(bits);
  return { ms, crcOk: crcOk && white > black && confidence >= MIN_CONFIDENCE, confidence };
}

// rgba: Uint8ClampedArray of the whole frame (frameW x frameH), or of a top-left region
// of it such as stampRegion(frameW, frameH). frameW/frameH are always the FRAME size.
export function readStamp(rgba, frameW, frameH) {
  const fail = { ms: null, crcOk: false, confidence: 0 };
  if (!rgba || typeof rgba.length !== 'number') return fail;
  const w = Math.floor(frameW);
  const h = Math.floor(frameH);
  if (!(w > 0 && h > 0)) return fail;
  const geo = bufferGeometry(rgba, w, h);
  if (!geo) return fail;

  // The encoder picks S from its own frame width; after simulcast downscaling the real cell
  // is S_host / factor (e.g. 34 / 4 = 8.5 px at 270 px), which neither the rounded nor the
  // max(8, ...) value matches. Try the exact w/32 too whenever it differs, and never accept
  // two CRC-valid reads that disagree (a CRC-6 match on a misaligned grid happens ~1/64).
  const primary = stampLayout(w, h).S;
  const sizes = [primary];
  const alt = w / 32;
  if (alt >= 2 && alt !== primary) sizes.push(alt);

  let best = null;
  let good = null;
  for (const S of sizes) {
    const r = readWithCellSize(rgba, geo.stride, geo.rows, S);
    if (!r) continue;
    if (r.crcOk) {
      if (good && good.ms !== r.ms) return fail;
      if (!good || r.confidence > good.confidence) good = r;
    }
    if (!best || r.confidence > best.confidence) best = r;
  }
  if (good) return good;
  return best ? { ...best, crcOk: false } : fail;
}

// Delay from a decoded stamp to the synced server time `nowServerMs`, handling the
// 2^24 ms wrap. Results above 2^23 are treated as negative (clock error).
export function unwrapDelay(nowServerMs, stampMs) {
  const now = ((Number(nowServerMs) % STAMP_MOD) + STAMP_MOD) % STAMP_MOD;
  let d = (((now - Number(stampMs)) % STAMP_MOD) + STAMP_MOD) % STAMP_MOD;
  if (d > HALF_MOD) d -= STAMP_MOD;
  return d;
}
